import React, { useEffect, useMemo, useState } from 'react';
import { Button, Typography, showToast } from '@attentive-platform/stem-ui';

import LookupModal from './LookupModal';
import UnlinkModal from './UnlinkModal';
import ConfirmLinkModal from './ConfirmLinkModal';
import {
  fetchCompanyLookup,
  fetchScopeRecord,
  linkCompanyToScopeRecord,
  scopeRecordHasKam,
  unlinkPreviousCompanyLinks
} from '../../ScopeConsole/api';
import { MODE_LOZENGE_COLOR, SOP_MODE_ENUM, SOP_MODE_LABELS, buildModes, resolveEffectiveMode } from './constants';
import { formatLocaleDate } from '../../../Utils/HelperFunctions';
import { ModeLozengeStyles, ScopeLinkageStyles as S } from './styles';

const summarizeScopeRecord = record => {
  const trades = Array.isArray(record?.trades_in_scope) ? record.trades_in_scope : [];
  return trades
    .map(t => {
      const sections = (Array.isArray(t.sections) ? t.sections : [])
        .map(s => ({
          id: s.section_id || s.id,
          name: s.section_name || s.name || '—',
          scopeCount: (s.assigned_scopes || s.scopes || []).length
        }))
        .filter(s => s.scopeCount > 0);
      return {
        id: t.trade_id || t.id,
        name: t.trade_name || t.name || '—',
        sections
      };
    })
    .filter(t => t.sections.length > 0);
};

const linkedFromScopeRecord = (record, { cardId, companyName, fallbackEmail, linker }) => {
  const tradeSummary = summarizeScopeRecord(record);
  const version = record?.current_version?.version;
  return {
    id: cardId || record?.id,
    company: companyName || record?.company_name || '—',
    email:
      record?.current_version?.actor_email ||
      record?.submitted_by_email ||
      record?.customer_email ||
      fallbackEmail ||
      '',
    trades: tradeSummary.length,
    tradeList: tradeSummary.map(t => t.name),
    tradesDetail: tradeSummary,
    version: version != null ? `v${version}` : null,
    items: record?.item_count ?? 0,
    linkedAt: record?.linked_at,
    linkedBy: record?.linked_by?.name || record?.linked_by?.email || linker || '—'
  };
};

const formatLinkedMeta = (linkedBy, linkedAt) => {
  if (!linkedAt) return linkedBy || '—';
  const d = formatLocaleDate(linkedAt);
  return d ? `${linkedBy || '—'} · ${d}` : linkedBy || '—';
};

const MODE_LOZENGE_MAP = {
  neutral: ModeLozengeStyles.Neutral,
  success: ModeLozengeStyles.Success,
  gold: ModeLozengeStyles.Gold
};

const ModeLozenge = ({ mode }) => {
  const Wrap = MODE_LOZENGE_MAP[MODE_LOZENGE_COLOR[mode]] || ModeLozengeStyles.Neutral;
  return <Wrap>{SOP_MODE_LABELS[mode] || mode}</Wrap>;
};

const CARD_VARIANT = {
  cta: S.ModeCardCta,
  current: S.ModeCardCurrent,
  idle: S.ModeCardIdle
};

const TAG_VARIANT = {
  cta: S.ModeTagCta,
  current: S.ModeTagCurrent,
  idle: S.ModeTagIdle
};

const TITLE_VARIANT = {
  cta: S.ModeTitleCta,
  current: S.ModeTitleIdle,
  idle: S.ModeTitleIdle
};

const STATUS_VARIANT = {
  current: S.ModeStatusChipCurrent,
  idle: S.ModeStatusChipIdle
};

const ModeCard = ({ mode, onLinkScope, onLegacyAction }) => {
  const Card = CARD_VARIANT[mode.variant] || S.ModeCardIdle;
  const Tag = TAG_VARIANT[mode.variant] || S.ModeTagIdle;
  const Title = TITLE_VARIANT[mode.variant] || S.ModeTitleIdle;
  const StatusChip = STATUS_VARIANT[mode.variant === 'current' ? 'current' : 'idle'];

  return (
    <Card>
      <S.ModeCardHead>
        <S.ModeTagRow>
          <Tag>{mode.tag}</Tag>
        </S.ModeTagRow>
        <Title>{mode.title}</Title>
        <Typography variant='body3' color='#4D4D4D' sx={{ lineHeight: 1.55 }}>
          {mode.body}
        </Typography>
      </S.ModeCardHead>
      <S.ModeCta>
        {mode.isCta ? (
          <Button variant='contained' color='primary' size='large' onClick={onLinkScope}>
            Link scope record
          </Button>
        ) : (
          <S.ModeStatusStack>
            <StatusChip>{mode.statusLabel}</StatusChip>
            {mode.hasAction && (
              <Button
                variant='outlined'
                color='primary'
                size='medium'
                disabled={!!mode.actionDisabled}
                onClick={onLegacyAction}>
                {mode.actionLabel}
              </Button>
            )}
          </S.ModeStatusStack>
        )}
      </S.ModeCta>
    </Card>
  );
};

const signedInLinker = () => {
  try {
    return (localStorage.getItem('userEmail') || '').trim() || '—';
  } catch {
    return '—';
  }
};

const ScopeLinkage = ({
  company,
  companyId,
  email,
  registeredOn,
  scopeRecordId,
  tier,
  onLegacySetup,
  hasLegacyTrades
}) => {
  const [serverMode, setServerMode] = useState(SOP_MODE_ENUM.FREE_FORM);
  const [linked, setLinked] = useState(null);
  const [showLookup, setShowLookup] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showUnlink, setShowUnlink] = useState(false);
  const [candidate, setCandidate] = useState(null);
  const [linking, setLinking] = useState(false);
  const [replacingRecord, setReplacingRecord] = useState(false);
  const [fetchingScope, setFetchingScope] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(!!email);
  const linker = signedInLinker();

  const isLinkedRecord = !!linked;
  const mode = useMemo(() => resolveEffectiveMode(serverMode, isLinkedRecord), [serverMode, isLinkedRecord]);
  const modes = useMemo(
    () => buildModes(mode, { hasLinkedRecord: isLinkedRecord, sopMode: serverMode }),
    [mode, isLinkedRecord, serverMode]
  );
  const gateOk = mode !== SOP_MODE_ENUM.FREE_FORM;

  useEffect(() => {
    if (!email) {
      setBootstrapping(false);
      return undefined;
    }
    let cancelled = false;
    setBootstrapping(true);

    (async () => {
      const { data, error } = await fetchCompanyLookup({ email });
      if (cancelled) return;
      if (error || !data) {
        setBootstrapping(false);
        return;
      }

      const cardId = data.card_id ?? data.company?.card_id ?? null;
      const apiLinked = data.linked === true || data.company?.linked === true;
      const nextServerMode = Object.values(SOP_MODE_ENUM).includes(data.scope_mode)
        ? data.scope_mode
        : SOP_MODE_ENUM.FREE_FORM;

      if (!apiLinked || !cardId) {
        setServerMode(nextServerMode);
        setLinked(null);
        setBootstrapping(false);
        return;
      }

      const { data: record, error: recordErr } = await fetchScopeRecord(cardId);
      if (cancelled) return;
      if (recordErr) {
        setServerMode(SOP_MODE_ENUM.FREE_FORM);
        setLinked(null);
        setBootstrapping(false);
        return;
      }

      const recordLinked = record?.linked === true || !!record?.company_id;
      if (!recordLinked) {
        setServerMode(nextServerMode);
        setLinked(null);
        setBootstrapping(false);
        return;
      }

      setLinked(
        linkedFromScopeRecord(record, {
          cardId,
          companyName: data.company?.name || company,
          fallbackEmail: data.company?.customer_email || email,
          // Don't invent attribution for historical links when API omits linked_by.
          linker: '—'
        })
      );
      setServerMode(SOP_MODE_ENUM.ROSETTA_VERSION);
      setBootstrapping(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [email, company]);

  const handlePick = async submission => {
    setCandidate(submission);
    setShowLookup(false);
    setShowConfirm(true);

    const cardId = submission?.card_id;
    if (!cardId) {
      showToast('No card id returned from company lookup', 'error');
      return;
    }
    if (replacingRecord && linked?.id && String(cardId) === String(linked.id)) {
      showToast('Pick a different scope record — this one is already linked', 'error');
      setShowConfirm(false);
      setShowLookup(true);
      return;
    }

    setFetchingScope(true);
    const { data, error } = await fetchScopeRecord(cardId);
    setFetchingScope(false);
    if (error) {
      const detail = typeof error === 'string' ? error : error.detail || error.error || 'Could not load scope record';
      showToast(detail, 'error');
      return;
    }
    setCandidate(prev => (prev?.card_id === cardId ? { ...prev, scopeRecord: data } : prev));
  };

  const handleConfirm = async () => {
    if (!candidate) return;
    const cardId = candidate.card_id || scopeRecordId || linked?.id;
    if (!cardId) {
      showToast('Missing card id from company lookup — try looking up the company again', 'error');
      return;
    }
    const pickedCompanyId = companyId;
    if (!pickedCompanyId) {
      setShowConfirm(false);
      showToast('Missing company id — look up this company on company scopes first, then try linking again', 'error');
      return;
    }
    if (!scopeRecordHasKam(candidate.scopeRecord)) {
      showToast(
        'This scope record has no Key Account Manager. Assign a KAM on the scope record before linking.',
        'error'
      );
      return;
    }
    setLinking(true);
    const previousRecordId = linked?.id && String(linked.id) !== String(cardId) ? linked.id : null;
    const { data, error } = await linkCompanyToScopeRecord({
      scopeRecordId: cardId,
      companyId: pickedCompanyId,
      cardId
    });
    if (error) {
      setLinking(false);
      const detail = typeof error === 'string' ? error : error.error || error.detail || 'Could not link company';
      showToast(detail, 'error');
      return;
    }
    const { errors: unlinkErrors } = await unlinkPreviousCompanyLinks({
      keepRecordId: data?.card_id || cardId,
      previousRecordId,
      companyId: pickedCompanyId,
      email: candidate.company?.customer_email || email
    });
    setLinking(false);
    if (unlinkErrors.length) {
      showToast('Linked the new record, but the previous intake is still linked', 'error');
    }
    const v = data?.linked_version || {};
    const companyName = data?.company?.name || candidate.company?.name || '—';
    const fromRecord = linkedFromScopeRecord(candidate.scopeRecord, {
      cardId: data?.card_id || cardId,
      companyName,
      fallbackEmail: candidate.company?.customer_email || '',
      linker
    });
    setLinked({
      ...fromRecord,
      email: v.submitted_by || fromRecord.email,
      trades: data?.trade_count ?? fromRecord.trades,
      version: v.version != null ? `v${v.version}` : fromRecord.version,
      items: data?.item_count ?? fromRecord.items,
      linkedAt: data?.linked_at || fromRecord.linkedAt,
      linkedBy: data?.linked_by?.name || data?.linked_by?.email || fromRecord.linkedBy
    });
    setServerMode(
      Object.values(SOP_MODE_ENUM).includes(data?.scope_mode_after)
        ? data.scope_mode_after
        : SOP_MODE_ENUM.ROSETTA_VERSION
    );
    setShowConfirm(false);
    setReplacingRecord(false);
    const superseded = data?.superseded_version;
    let toastMsg = `Linked ${companyName} as the scope of record`;
    if (superseded) {
      toastMsg = `Linked ${companyName} as the scope of record — replaced v${superseded.version}`;
    } else if (replacingRecord) {
      toastMsg = `Replaced the scope of record with ${companyName}`;
    }
    showToast(toastMsg, 'success');
  };

  const handleUnlinkConfirm = () => {
    setShowUnlink(false);
    setReplacingRecord(true);
    setShowLookup(true);
  };

  return (
    <S.Wrap>
      <S.Head>
        <S.Uppercase>Scope linkage</S.Uppercase>
        <S.TitleRow>
          <S.H2>{company || 'This company'}</S.H2>
          <ModeLozenge mode={mode} />
        </S.TitleRow>
        {(email || registeredOn) && (
          <Typography variant='body3' color='#666666'>
            {email}
            {email && registeredOn ? ' · ' : ''}
            {registeredOn ? `registered ${registeredOn}` : ''}
            {tier ? ` · ${tier}` : ''}
          </Typography>
        )}
      </S.Head>

      {bootstrapping ? (
        <Typography variant='body3' color='#808080'>
          Loading scope linkage…
        </Typography>
      ) : (
        <>
          {!isLinkedRecord && (
            <S.ModeCardsGrid>
              {modes.map(m => {
                const isLegacy = m.key === SOP_MODE_ENUM.LEGACY_SOP;
                const effectiveMode = isLegacy && hasLegacyTrades ? { ...m, actionLabel: 'Edit legacy SOP' } : m;
                return (
                  <ModeCard
                    key={m.key}
                    mode={effectiveMode}
                    onLinkScope={() => setShowLookup(true)}
                    onLegacyAction={() => {
                      if (!isLegacy || serverMode === SOP_MODE_ENUM.ROSETTA_VERSION) return;
                      if (serverMode !== SOP_MODE_ENUM.LEGACY_SOP) {
                        setServerMode(SOP_MODE_ENUM.LEGACY_SOP);
                        showToast(
                          hasLegacyTrades
                            ? 'Editing legacy SOP — edit trades below'
                            : 'Legacy SOP setup — edit trades below',
                          'info'
                        );
                      } else {
                        showToast('Editing legacy SOP — scroll down to the trade cards', 'info');
                      }
                      onLegacySetup && onLegacySetup();
                    }}
                  />
                );
              })}
            </S.ModeCardsGrid>
          )}

          {isLinkedRecord && (
            <S.RecordCard>
              <S.RecordHeader>
                <S.RecordHeaderLeft>
                  <Typography variant='body1' sx={{ fontWeight: 600 }}>
                    Scope of record
                  </Typography>
                  <Typography variant='body4' color='#666666'>
                    Replaces free-form for this company — takeoffs read from this record.
                  </Typography>
                </S.RecordHeaderLeft>
                <S.RecordHeaderActions>
                  <S.ConsoleLink
                    href={linked?.id ? `/scope-console?id=${encodeURIComponent(linked.id)}` : '/scope-console'}>
                    Open in scope console →
                  </S.ConsoleLink>
                  <Button variant='text' color='primary' size='medium' onClick={() => setShowUnlink(true)}>
                    Unlink
                  </Button>
                </S.RecordHeaderActions>
              </S.RecordHeader>
              <S.RecordBody>
                <S.MetaRow>
                  {[
                    { label: 'Current version', value: linked.version || 'v1' },
                    { label: 'Submitted by', value: linked.email || '—' },
                    {
                      label: 'Trades in scope',
                      value: `${linked.trades} trade${linked.trades === 1 ? '' : 's'}`
                    },
                    {
                      label: 'Linked by',
                      value: formatLinkedMeta(linked.linkedBy, linked.linkedAt)
                    }
                  ].map(m => (
                    <S.MetaCol key={m.label}>
                      <S.UpperLabel>{m.label}</S.UpperLabel>
                      <Typography variant='body2' sx={{ fontWeight: 500 }}>
                        {m.value}
                      </Typography>
                    </S.MetaCol>
                  ))}
                </S.MetaRow>
                <S.TradesBlock>
                  <Typography variant='body4' color='#808080'>
                    Trades in this scope
                  </Typography>
                  <S.TradeChips>
                    {linked.tradeList.map(t => (
                      <S.TradeChip key={t}>{t}</S.TradeChip>
                    ))}
                  </S.TradeChips>
                </S.TradesBlock>
              </S.RecordBody>
            </S.RecordCard>
          )}

          {gateOk ? (
            <S.GateOk>
              <S.GateOkIcon>✓</S.GateOkIcon>
              <Typography variant='body3' color='#4D4D4D' sx={{ lineHeight: 1.55 }}>
                Gate satisfied — this company can proceed with enterprise conversion.
              </Typography>
            </S.GateOk>
          ) : (
            <S.GateBlocked>
              <S.GateBlockedIcon>⚠</S.GateBlockedIcon>
              <Typography variant='body3' color='#4D4D4D' sx={{ lineHeight: 1.55 }}>
                Enterprise conversion needs one of the configured two: a linked scope record, or a legacy SOP. Free-form
                is the unconfigured default and doesn&apos;t satisfy the gate.
              </Typography>
            </S.GateBlocked>
          )}
        </>
      )}

      <LookupModal
        show={showLookup}
        onClose={() => {
          setShowLookup(false);
          setReplacingRecord(false);
        }}
        onPick={handlePick}
        initialQuery={email || ''}
        replacing={replacingRecord}
      />
      <ConfirmLinkModal
        show={showConfirm}
        onClose={() => {
          setShowConfirm(false);
          setReplacingRecord(false);
        }}
        onConfirm={handleConfirm}
        submission={candidate}
        company={company}
        submitting={linking}
        loadingScope={fetchingScope}
        replacing={replacingRecord}
      />
      <UnlinkModal show={showUnlink} onClose={() => setShowUnlink(false)} onConfirm={handleUnlinkConfirm} />
    </S.Wrap>
  );
};

export default ScopeLinkage;
